import { STATE_METHODS } from '@symbo.ls/state'
import { getSignals, isStore } from '@symbo.ls/signal'

// DOMQL subscribeTo handlers execute inside the signal observer that watches
// their declared keys. Reading unrelated state through the live proxy silently
// widens that dependency set and can turn a later write into a feedback
// cascade. This view reads from the raw backing object, but forwards state
// methods and assignments through the live proxy so writes remain reactive.
//
// Nested stores need the same treatment: a parent raw object retains the child
// snapshot it was created with, while the nested store owns later updates.
// Read each current signal with peek() and recursively wrap store values so
// nested reads stay current without subscribing and nested writes still reach
// the live store. Arrays and ordinary objects are not independent signal stores
// and can pass through unchanged.
const views = new WeakMap()

export const untrackedState = (state) => {
  const raw = state?.__raw
  if (!raw || typeof raw !== 'object') return state

  let view = views.get(state)
  if (view) return view

  const signals = isStore(state) ? getSignals(state) : null

  view = new Proxy(raw, {
    get(target, key) {
      if (STATE_METHODS.has(key)) return state[key]
      const signal = signals?.[key]
      const value = signal ? signal.peek() : Reflect.get(target, key)
      return isStore(value) ? untrackedState(value) : value
    },
    set(_target, key, value) {
      state[key] = value
      return true
    },
    deleteProperty(_target, key) {
      return delete state[key]
    }
  })
  views.set(state, view)
  return view
}
